import React from 'react';

/**
 * Loading placeholders, on the app's own tokens.
 *
 * Replaces a spinner with the shape of the thing being fetched, so a list that
 * is loading looks like a list rather than like an empty pane with a dot in it.
 *
 * The previous `Skeleton` in this folder wrapped MUI's and had no call sites at
 * all — it was the only reason `@mui/material` and `@emotion` were in the
 * bundle. This has no dependencies: `.syn-skeleton` in motion.css is a
 * background gradient sweep on a single element, so a placeholder costs one node.
 *
 * `Block` is the primitive; `Row` and `List` are the two shapes the app actually
 * loads — a conversation row and a column of them.
 */

const Block = ({ width, height = 12, radius, circle = false, style }) => (
  <span
    aria-hidden="true"
    className={`syn-skeleton${circle ? ' is-circle' : ' is-text'}`}
    style={{
      display: 'block',
      width: width ?? '100%',
      height,
      flex: '0 0 auto',
      ...(radius ? { borderRadius: radius } : null),
      ...style,
    }}
  />
);

/** One conversation row: avatar, then two lines of unequal length. */
const Row = ({ avatar = 40 }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 10px' }}>
    <Block circle width={avatar} height={avatar} />
    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <Block width="52%" height={11} />
      <Block width="78%" height={9} />
    </span>
  </span>
);

/**
 * A column of rows.
 *
 * Widths are varied per row from the index rather than at random: a skeleton
 * list whose lines are all the same length reads as a table, and `Math.random`
 * would reshuffle every row on every render.
 */
const List = ({ rows = 6, avatar = 40 }) => (
  <span
    role="status"
    aria-label="Loading"
    style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
  >
    {Array.from({ length: rows }, (_, i) => (
      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 10px' }}>
        <Block circle width={avatar} height={avatar} />
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Block width={`${46 + ((i * 13) % 26)}%`} height={11} />
          <Block width={`${64 + ((i * 17) % 24)}%`} height={9} />
        </span>
      </span>
    ))}
  </span>
);

/** Stream placeholder: alternating incoming/outgoing bubble shapes. */
const Stream = ({ rows = 5 }) => (
  <span
    role="status"
    aria-label="Loading messages"
    style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0' }}
  >
    {Array.from({ length: rows }, (_, i) => {
      const out = i % 2 === 1;
      return (
        <span key={i} style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start' }}>
          <Block
            width={`${38 + ((i * 11) % 28)}%`}
            height={i % 3 === 0 ? 52 : 34}
            radius={out ? 'var(--bubble-out)' : 'var(--bubble-in)'}
          />
        </span>
      );
    })}
  </span>
);

const Skeleton = { Block, Row, List, Stream };

export default Skeleton;
